import { getLanguage, location } from 'cdui-js';
import i18n from '../../../../i18n';
import { routeReplace } from '../../../../lib/browser';
import { showToast } from '../../components/Toast';
import { userState } from '../../../../stores/user';
import { appendDepositAppQuery } from './appBridge';

export type Currency = 'HKD' | 'USD' | 'CNY';

export type DepositQuery = {
  currency: Currency;
  accountNum: string;
  payeeBankAccountNum: string;
  payeeBankCode: string;
  payeeBankName: string;
};

const CURRENCIES: Currency[] = ['HKD', 'USD', 'CNY'];
const FORM_BLOCK_KEY = 'h5-deposit-block-form';

let marketBankCache: any[] = [];

export const setMarketBankCache = (list: any[]) => {
  marketBankCache = Array.isArray(list) ? list : [];
};

export const getMarketBankCache = () => marketBankCache;

export const readDepositQuery = (): DepositQuery => {
  const q = (location.query || {}) as Record<string, string>;
  const currency = CURRENCIES.includes(q.currency as Currency) ? (q.currency as Currency) : 'HKD';
  return {
    currency,
    accountNum: q.accountNum || '',
    payeeBankAccountNum: q.payeeBankAccountNum || '',
    payeeBankCode: q.payeeBankCode || '',
    payeeBankName: q.payeeBankName || '',
  };
};

export const depositUrl = (path: string, patch: Partial<DepositQuery> = {}) => {
  const q = { ...readDepositQuery(), ...patch };
  const params = new URLSearchParams();
  params.set('currency', q.currency || 'HKD');
  if (q.accountNum) params.set('accountNum', q.accountNum);
  if (q.payeeBankAccountNum) params.set('payeeBankAccountNum', q.payeeBankAccountNum);
  if (q.payeeBankCode) params.set('payeeBankCode', q.payeeBankCode);
  if (q.payeeBankName) params.set('payeeBankName', q.payeeBankName);
  appendDepositAppQuery(params);
  return `${path}?${params.toString()}`;
};

export const homeUrl = () => {
  const q = readDepositQuery();
  return depositUrl('/h5/deposits', {
    currency: q.currency,
    accountNum: q.accountNum,
    payeeBankAccountNum: '',
    payeeBankCode: '',
    payeeBankName: '',
  });
};

export const isChequePath = () => location.path.indexOf('/cheque/') >= 0;

export const blockFormReturn = () => {
  sessionStorage.setItem(FORM_BLOCK_KEY, '1');
};

export const shouldLeaveForm = () => sessionStorage.getItem(FORM_BLOCK_KEY) === '1';

export const clearFormBlock = () => {
  sessionStorage.removeItem(FORM_BLOCK_KEY);
};

/** 成功后进入目标页：isBackToApp 则直接替换为记录页；否则先换成首页再 push，避免回到已填表单 */
export const leaveSubmittedForm = (next: string) => {
  blockFormReturn();
  const backToApp = /(?:^|[?&])isBackToApp=1(?:&|$)/.test(next);
  if (backToApp) {
    routeReplace(next);
    return;
  }
  window.history.replaceState(window.history.state, '', homeUrl());
  location.routeTo(next);
};

const LIST_KEYS = ['list', 'result', 'records', 'rows', 'items', 'banks', 'data'];

/** 接口已由拦截器解开 data；再兼容 list / result 以及多包一层 */
export const unwrapList = (res: any, depth = 0): any[] => {
  if (Array.isArray(res)) return res;
  if (!res || typeof res !== 'object' || depth > 3) return [];
  for (const key of LIST_KEYS) {
    if (res[key] == null) continue;
    const value = res[key];
    if (Array.isArray(value)) return value;
    const nested = unwrapList(value, depth + 1);
    if (nested.length) return nested;
  }
  return [];
};

export const dash = (value?: string | null) => {
  const text = String(value ?? '').trim();
  return text || '-';
};

export const copyText = async (value?: string | null) => {
  const text = dash(value);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const input = document.createElement('textarea');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
  } catch {
    // 复制失败也提示，避免用户以为没点到
  }
  showToast({ message: i18n.FundH5.Copied, duration: 1500, position: 'bottom' });
};

const nameField = () => {
  const lang = String(getLanguage() || 'zh-CN').trim();
  if (lang === 'en' || lang === 'en-US' || lang.startsWith('en-')) return 'bankNameEN';
  if (lang === 'zh-TW' || lang === 'zh-HK' || lang === 'zh-Hant') return 'bankNameTC';
  return 'bankNameSC';
};

/** 字典命中按语言取名，没有再退回接口 bankName。收款页不主动拉字典 */
export const localizedBankName = (row: any, dict?: any[]) => {
  const code = String(row?.bankCode ?? row?.branchCode ?? '');
  const hit = code && dict?.length ? dict.find((item) => String(item?.bankCode) === code) : null;
  const source = hit || row || {};
  const named = String(source[nameField()] || '').trim();
  return named || String(row?.bankName || source.bankName || '').trim();
};

export const brokerAccountNum = (row: any) => String(row?.accountNum ?? row?.accountNumber ?? '');
export const brokerBankCode = (row: any) => String(row?.bankCode ?? row?.branchCode ?? '');
export const brokerBranchCode = (row: any) => String(row?.branchCode ?? row?.bankCode ?? '');

export const activeUserBanks = (list: any[]) => list.filter((item) => item?.approveStatus !== 'PENDING');

export const maskBankAccount = (num: string) => {
  const text = String(num || '');
  if (text.length < 8) return text;
  return `${text.slice(0, 4)}**** ****${text.slice(-4)}`;
};

/** 证券户 productType 为 S，缺省也当证券。加密户为 C。type 是现金/孖展，不能用来判断品类 */
export const isSecuritiesAccount = (row: any) => String(row?.productType ?? '').trim() !== 'C';

/**
 * 用 Token extra.mode 过滤品类，不看地址。modeType 不参与过滤。
 * virtual 只留 productType=C。security 留现金和孖展。空 mode 不过滤。
 */
export const matchDepositAccount = (row: any, mode: string) => {
  if (mode === 'virtual') return !isSecuritiesAccount(row);
  if (mode === 'security') return isSecuritiesAccount(row);
  return true;
};

export const accountNumberOf = (row: any) => String(row?.accountNum ?? row?.accountNumber ?? '').trim();

/** 先看 productType。C 是虚拟账户；证券户 type 为 M 是孖展，其余是现金。两个字段都可以是 C */
export const stockAccountLabel = (row: any) => {
  const kind = !isSecuritiesAccount(row)
    ? i18n.FundH5.AccountVirtual
    : String(row?.type ?? '').trim() === 'M'
      ? i18n.FundH5.AccountMargin
      : i18n.FundH5.AccountCash;
  return `${kind}-${accountNumberOf(row)}`;
};

export const accountSubUserId = (row: any) => String(row?.subUserId ?? row?.subUserID ?? '').trim();

const nonempty = (value: unknown) => String(value ?? '').trim();

/** 户号在 /user/info 的 tradePermission 上，空字符串当没有该户 */
export const tradePermissionOf = (userInfo: any) => {
  const root = userInfo?.data ?? userInfo ?? {};
  const raw = root?.tradePermission;
  return raw && typeof raw === 'object' ? raw : {};
};

/** 入口 URL 有 accountNum 就用它；否则现金户，再没有用融资户 */
export const preferredAccountNum = (urlAccountNum?: string) => {
  const fromUrl = nonempty(urlAccountNum);
  if (fromUrl) return fromUrl;
  const perm = tradePermissionOf(userState.userInfo);
  return nonempty(perm.cashAccountNum) || nonempty(perm.marginAccountNum);
};

/** 证券户在前，加密户在后 */
export const sortDepositAccounts = (list: any[]) => {
  const securities: any[] = [];
  const crypto: any[] = [];
  list.forEach((item) => {
    if (isSecuritiesAccount(item)) securities.push(item);
    else crypto.push(item);
  });
  return [...securities, ...crypto];
};

/** overview 可能是扁平数组，也可能还包在 account 里 */
export const overviewAccountsOf = (res: any) => {
  const list = unwrapList(res);
  const nested = list.some((item) => Array.isArray(item?.account) && !accountNumberOf(item));
  const flat = nested ? list.flatMap((item) => item.account || []) : list;
  return flat.filter((item) => accountNumberOf(item));
};

/** 业务码 90001：2go 未登录。拦截器把 code 和 message 拼成字符串 */
export const is2goNotLoggedIn = (error: unknown) => {
  if (error == null) return false;
  if (typeof error === 'object') {
    const code = (error as { code?: unknown }).code;
    if (code != null && String(code) === '90001') return true;
  }
  return /^\s*90001\b/.test(String(error));
};

/** 只留数字和一个小数点；整数最多 15 位；小数最多 4 位；开头的点补成 0. */
export const sanitizeAmount = (input: string) => {
  let text = String(input ?? '').replace(/[^\d.]/g, '');
  if (!text) return '';
  const dot = text.indexOf('.');
  let intPart = dot >= 0 ? text.slice(0, dot) : text;
  const decPart = dot >= 0 ? text.slice(dot + 1).replace(/\./g, '') : null;
  if (dot === 0) intPart = '0';
  intPart = intPart.replace(/^0+(?=\d)/, '');
  if (!intPart) intPart = '0';
  if (intPart.length > 15) intPart = intPart.slice(0, 15);
  if (decPart == null) return intPart;
  return `${intPart}.${decPart.slice(0, 4)}`;
};

export const amountError = (value: string) => {
  if (!String(value || '').trim()) return i18n.H5Deposit.ErrDepositAmountEmpty;
  if (!/^\d+(\.\d{1,4})?$/.test(value) || Number(value) <= 0) return i18n.H5Deposit.ErrAmountInvalid;
  return '';
};

export const errorText = (error: any) => String(error?.message || error?.data?.message || '').trim();
